import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { faBackwardFast } from "@fortawesome/pro-solid-svg-icons/faBackwardFast";
import { faForwardFast } from "@fortawesome/pro-solid-svg-icons/faForwardFast";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { AppTransComponents } from "app/appTrans";
import { MprcContainer, MprcInfiniteLoading, MprcMessageBox, MprcRequestErrorReport, MprcSpinnerIndicator, MprcText } from 'components';
import { MprcFilterBox, MprcFilterLine } from "components/filter";
import { MprcRibbonScroll } from "components/MprcRibbonScroll";
import MprcUserNameView from "components/MprcUserNameView";
import { GetListPublicationInPlaybackResponse, ListPublicationItemsDto, PublicationType } from 'generated/CoreApi_v1';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from "react-router";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { ListPlaybackViewer } from "views/knowledge/publication/pages/List/ListPlaybackViewer";
import { PlaybackOptions } from "views/knowledge/publication/pages/List/MenuOptions";
import { PublicationChoice } from "views/knowledge/publication/pages/List/PublicationChoice";
import { PostPlaybackViewer } from "views/knowledge/publication/pages/Post/PostPlaybackViewer";
import { QuestionPlaybackViewer } from "views/knowledge/publication/pages/Question/QuestionPlaybackViewer";
import { PublicationHeader } from '../components/PublicationHeader';

////////////////////////////////////////////////////////////////////////////////////////////////

export const ListPlayback: React.FC = () => {

    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");
    const { width } = useWindowSize({ wait: 50 });

    const [playback, setPlayback] = useState<GetListPublicationInPlaybackResponse>();
    const [loading, setLoading] = useState<boolean>(true);
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [showSupressMessage, setShowSupressMessage] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [selectedPub, setSelectedPub] = useState<ListPublicationItemsDto | null>(null);
    const publicationId = useRef<string | null>(null);
    const selectedRef = useRef<HTMLElement | undefined>(undefined);

    ///
    const load = async (publicationId: string) => {
        try {
            const result = await api.v1.knowledgePublication.getListPublicationInPlayback({ listPublicationId: publicationId });
            const record = result.data;

            setPlayback(record);

            if (record.listItems.length > 0) {
                const prevSelectedIndex = location.state?.pubSelectedIndex ?? 0;
                const index = prevSelectedIndex < record.listItems.length ? prevSelectedIndex : 0;
                handleSelectItem(record.listItems[index], index);
            }
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const handleSelectItem = (pubItem: ListPublicationItemsDto, index: number) => {
        setSelectedIndex(index);
        setSelectedPub(pubItem);
        navigate(document.location.search, { replace: true, state: { pubSelectedIndex: index } });

        const el = document.getElementById(pubItem.publicationId);
        if (el) selectedRef.current = el;
    };

    ///
    const handleMove = (newIndex: number) => {
        if (playback && playback.listItems.length > newIndex) {
            const nextPublication = playback.listItems[newIndex];
            handleSelectItem(nextPublication, newIndex);
        }
    };

    ///
    const handlePublicationUpdate = (e: FeedCustomEventProps) => {
        if (e.detail == publicationId.current) {
            load(publicationId.current!);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        subscribe("PublicationUpdate", handlePublicationUpdate);
        return () => {
            unsubscribe("PublicationUpdate", handlePublicationUpdate);
        };
    }, []);

    ///
    useEffect(() => {
        if (params.id) publicationId.current = params.id;
        if (publicationId.current != null) {
            // Carrega lista.
            load(publicationId.current);
        }
    }, [document.location.pathname]);

    /// Reposiciona Ribbon
    useEffect(() => {
        if (selectedPub) {
            const el = document.getElementById(selectedPub.publicationId);
            if (el && selectedRef.current != el) selectedRef.current = el;
        }
    }, [selectedPub]);


    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderSupressMessage = (
        <Box mb={1}>
            <MprcMessageBox
                message={
                    <>
                        <Typography variant="caption" component="span" >
                            <Trans
                                i18nKey="Publication.ListPlayback.SupressMessage"
                                t={t}
                                components={AppTransComponents}
                            />
                        </Typography>

                    </>
                }
                fullWidth
                variant="notice"
            />
        </Box>
    );

    ///
    const RenderEmptyMessage = (
        <Box mb={1}>
            <MprcMessageBox
                message={t("Publication.ListPlayback.EmptyMessage")}
                fullWidth
                variant="info"
            />
        </Box>
    );

    ///
    const RenderTitle = useCallback(() => {

        if (playback == undefined)
            return null;

        return (
            <Grid container alignItems="flex-start" columnSpacing={1} wrap="nowrap">
                <Grid>
                    <Grid container alignItems="flex-start" columnSpacing={1} wrap="nowrap">
                        <Grid>
                            <Typography variant="h3">{playback?.list?.title}</Typography>
                        </Grid>
                        <Grid>
                            <PlaybackOptions
                                publication={playback.list}
                                onChanged={() => load(publicationId.current!)}
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption">{`${t("Publication.ListPlayback.CreatedByText")} `}</Typography>
                    <Typography
                        variant="inherit" component="span"
                        sx={
                            (theme) => ({
                                ...theme.extended.feed["authorPrimary"]
                            })
                        }
                    >
                        <MprcUserNameView user={playback?.list?.author} />
                    </Typography>
                </Grid>
            </Grid>
        );
    }, [playback, publicationId]);

    ///
    const RenderNavigation = useCallback(() => {

        if (playback == undefined)
            return null;

        return (
            <Grid container columnSpacing={2} alignItems="center">
                <Grid>
                    <Box
                        sx={{
                            cursor: "pointer",
                            "&:hover": {
                                color: theme.palette["muted"].main,
                            }
                        }}
                        onClick={() => handleMove(selectedIndex - 1)}
                    >
                        <MprcText color={["muted", selectedIndex == 0 ? "light" : "main"]}>
                            <FontAwesomeIcon icon={faBackwardFast} size="xl" />
                        </MprcText>
                    </Box>
                </Grid>
                <Grid>
                    <Box sx={{ minWidth: "3rem", textAlign: "center" }}>
                        <Typography variant="subtitle1">{`${selectedIndex + 1} / ${playback.listItems.length}`}</Typography>
                    </Box>
                </Grid>
                <Grid>
                    <Box
                        sx={{
                            cursor: "pointer"
                        }}
                        onClick={() => handleMove(selectedIndex + 1)}
                    >
                        <MprcText color={["muted", selectedIndex == playback.listItems.length - 1 ? "light" : "main"]}>
                            <FontAwesomeIcon icon={faForwardFast} size="xl" />
                        </MprcText>
                    </Box>
                </Grid>
            </Grid>
        );
    }, [playback, selectedIndex]);

    ///
    const RenderItems = useCallback(() => {

        if (playback == undefined)
            return null;

        return (
            <MprcRibbonScroll selectedRef={selectedRef.current} showScrollbar slideWidth={400}>
                {
                    loading
                        ?
                        <Box sx={{ mt: 0.5, width: 100 }}>
                            <MprcSpinnerIndicator />
                        </Box>
                        :
                        playback?.listItems.map((item, index) =>
                            <PublicationChoice
                                id={item.publicationId}
                                key={item.publicationId}
                                label={`${index + 1}. ${item.title}`}
                                selected={selectedPub?.publicationId == item.publicationId}
                                onClick={() => handleSelectItem(item, index)}
                            />
                        )
                }
            </MprcRibbonScroll>
        );
    }, [loading, playback, selectedPub]);

    ///
    const RenderSupressIcon = useCallback(() => {

        if (playback == undefined)
            return null;

        return (
            <Box onClick={() => setShowSupressMessage(!showSupressMessage)}>
                <MprcText color={"warning"}>
                    <FontAwesomeIcon icon={faTriangleExclamation} size="xl" />
                </MprcText>
            </Box>
        );
    }, [playback]);

    ///
    const RenderHeader = () => {

        if (playback == undefined)
            return null;

        return (
            <MprcFilterBox>
                <MprcFilterLine>
                    <Box sx={{ width: "100%" }}>
                        <Grid container columnSpacing={3}>
                            <Grid size="grow">
                                {RenderTitle()}
                            </Grid>

                            <Grid>
                                {
                                    playback.hasSupressed &&
                                    RenderSupressIcon()
                                }
                            </Grid>

                            <Grid>
                                {
                                    playback?.listItems.length > 0 &&
                                    RenderNavigation()
                                }
                            </Grid>

                        </Grid>

                        {RenderItems()}

                    </Box>

                </MprcFilterLine>
            </MprcFilterBox>
        );
    };

    ///
    return (
        <>
            {viewError && <MprcRequestErrorReport error={viewError} />}
            <MprcContainer position="sticky" margin="none">
                <PublicationHeader
                    onClose={gobackOrHome}
                    actions={
                        width < theme.breakpoints.values["md"]
                            ? [<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]
                            : []
                    }
                />
            </MprcContainer>
            {loading
                ? <MprcInfiniteLoading visible />
                : playback && <MprcContainer>{RenderHeader()}</MprcContainer>
            }

            {
                showSupressMessage && playback?.hasSupressed &&
                RenderSupressMessage
            }

            {
                playback?.listItems.length == 0 &&
                RenderEmptyMessage
            }

            {
                !loading && playback && selectedPub && selectedPub.publicationType == PublicationType.Post &&
                <PostPlaybackViewer
                    publicationId={selectedPub.publicationId}
                    openRightPanel={openRightPanel}
                    rightPanelBreakpoint="md"
                    onCloseRightPanel={() => setOpenRightPanel(false)}
                />
            }

            {
                !loading && playback && selectedPub && selectedPub.publicationType == PublicationType.List &&
                <ListPlaybackViewer
                    publicationId={selectedPub.publicationId}
                    openRightPanel={openRightPanel}
                    rightPanelBreakpoint="md"
                    onCloseRightPanel={() => setOpenRightPanel(false)}
                />
            }

            {
                !loading && playback && selectedPub && selectedPub.publicationType == PublicationType.Poll &&
                <PostPlaybackViewer
                    publicationId={selectedPub.publicationId}
                    openRightPanel={openRightPanel}
                    rightPanelBreakpoint="md"
                    onCloseRightPanel={() => setOpenRightPanel(false)}
                />
            }

            {
                !loading && playback && selectedPub && selectedPub.publicationType == PublicationType.Question &&
                <QuestionPlaybackViewer
                    publicationId={selectedPub.publicationId}
                    openRightPanel={openRightPanel}
                    onCloseRightPanel={() => setOpenRightPanel(false)}
                    rightPanelBreakpoint="md"
                />
            }

            {
                !loading && playback && selectedPub && selectedPub.publicationType == PublicationType.Answer &&
                <QuestionPlaybackViewer
                    publicationId={selectedPub.questionId!}
                    openRightPanel={openRightPanel}
                    answerId={selectedPub.publicationId}
                    onCloseRightPanel={() => setOpenRightPanel(false)}
                    rightPanelBreakpoint="md"
                />
            }
        </>
    );
};
